import React, { useEffect, useMemo, useRef, useState } from "react";

import DateBox from "devextreme-react/date-box";
import PositionsSPTable from "./PositionsSPTable";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import jsPDF from "jspdf";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import PositionsSPPivotTable from "./PositionsSPPivotTable";
import useCobDate from "../../hooks/useCobDate";

interface GridHandle {
  getInstance: () => any;
  getData: () => any[];
}

const fileFmt = (d: unknown): string =>
  d instanceof Date && !isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    : "unknown-date";

export default function PostionsSP(): React.ReactElement {
  const [cobDate, setCobDate, cobLoading] = useCobDate("/positions_sp_latest_cob");
  const [, , , latestCobDate] = useCobDate("/market_risk/sp_matrix_latest_cob");
  const [cobDateTotal, setCobDateTotal] = useState<Date | null>(null);
  const [compareDate, setCompareDate] = useState<Date | null>(null);

  const gridRef1 = useRef<GridHandle | null>(null);
  const gridRef2 = useRef<GridHandle | null>(null);

  const handlePdfExport = async (): Promise<void> => {
    const g1 = gridRef1.current?.getInstance();
    const g2 = gridRef2.current?.getInstance();
    if (!g1 && !g2) return;

    const doc = new jsPDF();

    const fmtDate = (d: unknown): string =>
      d instanceof Date && !isNaN(d.getTime())
        ? d.toLocaleDateString("en-GB")
        : "";

    const numberFormatter = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    let y = 15;

    doc.setFontSize(10);
    doc.setFont(undefined as any, "bold");
    doc.text("CoB:", 10, y);
    doc.text(fmtDate(cobDate), 30, y);

    if (compareDate) {
      y += 6;
      doc.text("Comparison:", 10, y);
      doc.text(fmtDate(compareDate), 30, y);
    }

    const customizeCell = ({ gridCell, pdfCell }: any): void => {
      if (!gridCell) return;

      if (gridCell.rowType === "header") {
        pdfCell.font = { style: "bold" };
        pdfCell.horizontalAlign = "center";
        return;
      }

      if (gridCell.rowType === "data") {
        const isTotalRow =
          gridCell.data &&
          (gridCell.data.assettype === "Total" ||
            gridCell.data.assettype === "Grand Total");

        if (gridCell.value != null && gridCell.value !== "") {
          const num = Number(gridCell.value);
          if (!Number.isNaN(num)) {
            if (num < 0) {
              pdfCell.text = `(${numberFormatter.format(Math.abs(num))})`;
              pdfCell.textColor = "#FF0000";
            } else {
              pdfCell.text = numberFormatter.format(num);
            }
          }
        }

        if (gridCell.column && gridCell.column.dataField === "assettype") {
          pdfCell.horizontalAlign = "left";
        } else {
          pdfCell.horizontalAlign = "center";
        }

        if (isTotalRow) {
          pdfCell.font = { style: "bold" };
          pdfCell.backgroundColor = "#007BFF";
          pdfCell.textColor = "#FFFFFF";
        }
      }
    };

    let tableTop = y + 10;

    if (g1) {
      await exportDataGridToPdf({
        jsPDFDocument: doc,
        component: g1,
        topLeft: { x: 10, y: tableTop },
        customizeCell,
      });
    }

    if (g2) {
      doc.addPage();
      let y2 = 15;
      doc.setFontSize(10);
      doc.setFont(undefined as any, "bold");
      doc.text("CoB:", 10, y2);
      doc.text(fmtDate(cobDate), 30, y2);
      const tableTop2 = y2 + 10;

      await exportDataGridToPdf({
        jsPDFDocument: doc,
        component: g2,
        topLeft: { x: 10, y: tableTop2 },
        customizeCell,
      });
    }

    doc.save(`structured products positions ${fileFmt(cobDate)}.pdf`);
  };

  const handleExcelExport = async (): Promise<void> => {
    const g1 = gridRef1.current?.getInstance();
    const g2 = gridRef2.current?.getInstance();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Report");

    const fmtDate = (d: unknown): string =>
      d instanceof Date && !isNaN(d.getTime())
        ? d.toLocaleDateString("en-GB")
        : "";

    const cobLabelCell = worksheet.getCell(1, 1);
    const cobValueCell = worksheet.getCell(1, 2);
    cobLabelCell.value = "CoB:";
    cobValueCell.value = fmtDate(cobDate);
    cobLabelCell.font = { bold: true };
    cobValueCell.font = { bold: true };

    let nextRow = 2;

    if (compareDate) {
      const cmpLabelCell = worksheet.getCell(2, 1);
      const cmpValueCell = worksheet.getCell(2, 2);
      cmpLabelCell.value = "Comparison:";
      cmpValueCell.value = fmtDate(compareDate);
      cmpLabelCell.font = { bold: true };
      cmpValueCell.font = { bold: true };
      nextRow = 3;
    }

    let row = nextRow + 1;

    const customizeCell = ({ gridCell, excelCell }: any): void => {
      if (!gridCell) return;

      if (gridCell.rowType === "header") {
        excelCell.font = { bold: true };
        excelCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        return;
      }

      if (gridCell.rowType === "data") {
        const isTotalRow =
          gridCell.data &&
          (gridCell.data.assettype === "Total" ||
            gridCell.data.assettype === "Grand Total");

        if (gridCell.value != null && gridCell.value !== "") {
          const num = Number(gridCell.value);
          if (!Number.isNaN(num)) {
            excelCell.value = num;
            excelCell.numFmt = "#,##0.00;[Red](#,##0.00)";
          }
        }

        if (isTotalRow) {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF007BFF" },
          };
        } else {
          excelCell.alignment = { horizontal: "center", vertical: "middle" };
        }
      }
    };

    if (g1) {
      await exportDataGrid({
        component: g1,
        worksheet,
        topLeftCell: { row, column: 1 },
        customizeCell,
      });
      row = (worksheet as any).lastRow.number + 2;
    }

    const cobTotalLabelCell = worksheet.getCell(row, 1);
    const cobTotalValueCell = worksheet.getCell(row, 2);
    cobTotalLabelCell.value = "CoB:";
    cobTotalValueCell.value = fmtDate(cobDate);
    cobTotalLabelCell.font = { bold: true };
    cobTotalValueCell.font = { bold: true };

    row = row + 1;

    if (g2) {
      await exportDataGrid({
        component: g2,
        worksheet,
        topLeftCell: { row, column: 1 },
        customizeCell,
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `structured products positions ${fileFmt(cobDate)}.xlsx`
    );
  };

  // Sync cobDateTotal with initial cobDate from the hook
  useEffect(() => {
    if (cobDate && !cobDateTotal) setCobDateTotal(cobDate);
  }, [cobDate, cobDateTotal]);

  return (
    <div
      style={{
        height: "calc(90vh - var(--header-h))",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gridTemplateColumns: "1fr",
        gap: 8,
        boxSizing: "border-box",
        overflow: "hidden",
        paddingLeft: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 12,
          marginTop: 12,
        }}
      >
        <div className="filter-block">
          <div className="filters-group">
            <label>CoB:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={cobDate}
              onValueChanged={(e: any) => setCobDate(e.value)}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>

          <div className="filters-group">
            <label>Comparison:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={compareDate}
              onValueChanged={(e: any) => setCompareDate(e.value)}
              width={170}
              max={latestCobDate ?? undefined}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto auto 1fr",
          gap: 8,
          overflow: "hidden",
          background: "transparent",
          minHeight: 0,
        }}
      >
        <div style={{ overflow: "auto", maxHeight: "30vh" }}>
          <PositionsSPTable ref={gridRef1} cobDate={cobDate} compareDate={compareDate} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: 0,
            fontWeight: 600,
            fontSize: 12,
            gap: 12,
          }}
        >
          <div className="filter-block">
            <div className="filters-group">
              <label>CoB:</label>
              <DateBox
                displayFormat="dd/MM/yyyy"
                useMaskBehavior
                pickerType="calendar"
                value={cobDateTotal}
                onValueChanged={(e: any) => setCobDateTotal(e.value)}
                width={180}
                style={{ flex: "0 0 auto" }}
                max={latestCobDate ?? undefined}
              />
            </div>
          </div>
          <button
            onClick={handleExcelExport}
            style={{
              padding: "10px 18px",
              borderRadius: "4px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Export to Excel
          </button>
          <button
            onClick={handlePdfExport}
            style={{
              padding: "10px 18px",
              borderRadius: "4px",
              backgroundColor: "#d9534f",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Export to PDF
          </button>
        </div>

        <div style={{ overflow: "auto", minHeight: 0 }}>
          <PositionsSPPivotTable ref={gridRef2} page="totals" cobDate={cobDateTotal} compareDate={null} />
        </div>
      </div>
    </div>
  );
}
